import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { Meeting, MeetingSummaryDTO, PracticeComparisonResponse } from '../api/meetingService';
import { getMeetingById, getMeetings, getMeetingPracticeComparison } from '../api/meetingService';
import type { GetMeetingsParams, Paginated } from '@/types/meeting';

type MeetingDetails = Meeting & { summary: string };

export const useMeetings = (params: GetMeetingsParams) => {
  return useQuery<Paginated<Meeting>, Error>({
    queryKey: ['meetings', params],
    queryFn: () => getMeetings(params),
    placeholderData: (prev) => prev,
  });
};

export const useMeetingById = (
  meetingId: string | undefined,
  summaryOnly = false,
  enabled = true,
): UseQueryResult<MeetingDetails, Error> =>
  useQuery({
    queryKey: ['meeting', meetingId, summaryOnly],
    queryFn: () => getMeetingById(meetingId, summaryOnly),
    enabled: Boolean(meetingId) && enabled,
    select: (raw) =>
      summaryOnly
        ? { ...(raw as Meeting), summary: (raw as MeetingSummaryDTO).summary }
        : { ...(raw as Meeting), summary: (raw as Meeting).ai_summary },
  });

export const useMeetingPracticeComparison = (
  meetingId: string | undefined,
  initialData?: PracticeComparisonResponse,
): UseQueryResult<PracticeComparisonResponse, Error> =>
  useQuery({
    queryKey: ['meeting-practice-comparison', meetingId],
    queryFn: () => getMeetingPracticeComparison(meetingId!),
    enabled: Boolean(meetingId),
    initialData,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });